import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

const CHANGE_PASSWORD_PATH = '/cambiar-password';

function FullPageLoader(): JSX.Element {
  return (
    <div
      style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}
      role="status"
      aria-live="polite"
    >
      <div className="inline muted">
        <span className="spinner" aria-hidden="true" />
        Verificando la sesión…
      </div>
    </div>
  );
}

/**
 * Ruta privada.
 *
 * La sesión se resuelve con `GET /api/auth/me`; hasta entonces no se decide nada,
 * porque redirigir antes de saberlo expulsaría a un usuario válido en cada recarga.
 *
 * Si `mustChangePassword` es true, TODA navegación se desvía al cambio de
 * contraseña.
 */
export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const { user, isResolved } = useAuth();
  const location = useLocation();

  if (!isResolved) return <FullPageLoader />;

  if (user === null) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (user.mustChangePassword && location.pathname !== CHANGE_PASSWORD_PATH) {
    return <Navigate to={CHANGE_PASSWORD_PATH} replace />;
  }

  return <>{children}</>;
}

/** Pantallas públicas: un usuario con sesión activa no debería volver al login. */
export function RedirectIfAuthenticated({ children }: { children: ReactNode }): JSX.Element {
  const { user, isResolved } = useAuth();

  if (!isResolved) return <FullPageLoader />;
  if (user !== null) {
    return <Navigate to={user.mustChangePassword ? CHANGE_PASSWORD_PATH : '/'} replace />;
  }
  return <>{children}</>;
}
